import { Signal } from '@latitude/signal';
import { Tooltip } from '@latitude/tooltip';
import type { ToggleGroupButtonItemComponentProps } from 'types';
import { useDeepCompareMemo } from 'use-deep-compare';

/**
 * ToggleGroupItemButton component renders a toggle button.
 * @param {ToggleGroupItemComponentProps} props - The props for the component.
 * @returns {JSX.Element} The ToggleGroupItemButton component.
 */
export const ToggleGroupItemButton = <T,>({
  selected,
  item,
  componentTriggerType: _componentTriggerType,
  analytics,
  Component,
  ...rest
}: ToggleGroupButtonItemComponentProps<T>) => {
  const {
    id,
    label,
    disabled = false,
    onRemove,
    tooltipLabelFormatter = (value) => value.label,
    selectedValueFormatter = (value) => value.id,
    icon,
    leadingIcon,
    signalStatus,
    alwaysShowTooltip = true,
  } = item;

  const shouldShowTooltip = !!selected || alwaysShowTooltip;

  // Computed in-render (not via useState+effect) so the Tooltip-wrapped vs
  // bare <span> choice below is stable from the first paint — deferring it to
  // a post-mount effect used to swap the wrapper (and recreate this button's
  // DOM node) one render after mount, which could drop a click landing in
  // that window.
  const tooltipContent = useDeepCompareMemo(
    () =>
      shouldShowTooltip ? tooltipLabelFormatter(selected || item) : undefined,
    [item, selected, shouldShowTooltip, tooltipLabelFormatter],
  );

  const onClickHandler = () => {
    const { onClick, ...newItem } = item;
    const onClickPayload = { ...newItem, value: selectedValueFormatter(item) };
    analytics('toggle_selected_item', onClickPayload);

    if ('onClick' in item) {
      onClick?.(onClickPayload);
    }
  };

  const itemComponent = (
    <Component
      {...rest}
      disabled={disabled}
      aria-pressed={!!selected}
      data-pressed={!!selected}
      selected={!!selected}
      leadingElement={leadingIcon}
      onClick={onClickHandler}
      onRemove={onRemove}
    >
      {label}
      {icon || null}
      {signalStatus && (
        <span className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/4">
          <Signal status={signalStatus} border />
        </span>
      )}
    </Component>
  );

  if (shouldShowTooltip && tooltipContent) {
    return (
      <Tooltip key={id} text={tooltipContent}>
        {itemComponent}
      </Tooltip>
    );
  }
  return itemComponent;
};
